import { cachedDashboardGet } from "./dashboardRequestCache";
import {
  InventorySnapshotResponse,
  OperationalInsightsResponse,
  PerformanceOverviewResponse,
  SalesIndividualPeriod,
  SalesPipelineApiStage,
} from "./salesIndividualDashboardApi";

export type ServiceIndividualPeriod = SalesIndividualPeriod;

export interface ServiceIndividualFilterParams {
  period: ServiceIndividualPeriod;
  from_date?: string;
  to_date?: string;
}

export interface ServiceIndividualKpiApiItem {
  id: string;
  label: string;
  helper_label?: string;
  helperLabel?: string;
  value: number | string;
}

export interface ServiceIndividualKpiResponse {
  period: ServiceIndividualPeriod;
  date_range: {
    from_date: string;
    to_date: string;
  };
  kpis: ServiceIndividualKpiApiItem[];
  pipeline_stages?: SalesPipelineApiStage[];
}

interface ApiResponseEnvelope<T> {
  status: number;
  success: boolean;
  message: string;
  data: T;
}

const buildServiceIndividualParams = (filters: ServiceIndividualFilterParams) => {
  const params: Record<string, string> = {
    period: filters.period,
  };

  if (filters.period === "custom") {
    if (filters.from_date) params.from_date = filters.from_date;
    if (filters.to_date) params.to_date = filters.to_date;
  }

  return params;
};

export const fetchServiceIndividualKpis = async (
  filters: ServiceIndividualFilterParams,
): Promise<ServiceIndividualKpiResponse> => {
  const response = await cachedDashboardGet<
    ApiResponseEnvelope<ServiceIndividualKpiResponse>
  >("/dashboard/service/individual/kpis",
    buildServiceIndividualParams(filters),
  );

  return response.data;
};

export type { PerformanceOverviewResponse };

export const fetchServiceIndividualPerformance = async (
  filters: ServiceIndividualFilterParams,
): Promise<PerformanceOverviewResponse> => {
  const response = await cachedDashboardGet<
    ApiResponseEnvelope<PerformanceOverviewResponse>
  >("/dashboard/service/individual/performance",
    buildServiceIndividualParams(filters),
  );

  return response.data;
};

export type { InventorySnapshotResponse };

export const fetchServiceIndividualInventorySnapshot = async (
  filters: ServiceIndividualFilterParams,
): Promise<InventorySnapshotResponse> => {
  const response = await cachedDashboardGet<
    ApiResponseEnvelope<InventorySnapshotResponse>
  >("/dashboard/service/individual/inventory-snapshot",
    buildServiceIndividualParams(filters),
  );

  return response.data;
};

export type { OperationalInsightsResponse };

export const fetchServiceIndividualOperationalInsights = async (
  filters: ServiceIndividualFilterParams,
): Promise<OperationalInsightsResponse> => {
  const response = await cachedDashboardGet<
    ApiResponseEnvelope<OperationalInsightsResponse>
  >("/dashboard/service/individual/operational-insights",
    buildServiceIndividualParams(filters),
  );

  return response.data;
};

export interface ServiceCustomerDetailsRow {
  id: string;
  customerName: string;
  serviceOrderNo: string;
  orderDate: string;
  productName: string;
  serialNumber: string;
  makeModel: string;
  reportedComplaint: string;
  serviceType: string;
  scheduleDate: string;
  status: string;
}

export interface ServiceCustomerDetailsResponse {
  period: ServiceIndividualPeriod;
  date_range: {
    from_date: string;
    to_date: string;
  };
  rows: ServiceCustomerDetailsRow[];
}

export interface ServiceValueCustomersResponse {
  period: ServiceIndividualPeriod;
  date_range: {
    from_date: string;
    to_date: string;
  };
  customers: import("../components/LowValueCustomer/types").LowValueCustomerRow[];
  highValueCustomers: import("../components/LowValueCustomer/types").LowValueCustomerRow[];
}

export const fetchServiceIndividualCustomerDetails = async (
  filters: ServiceIndividualFilterParams,
): Promise<ServiceCustomerDetailsResponse> => {
  const response = await cachedDashboardGet<
    ApiResponseEnvelope<ServiceCustomerDetailsResponse>
  >("/dashboard/service/individual/customer-details",
    buildServiceIndividualParams(filters),
  );

  return response.data;
};

export const fetchServiceIndividualValueCustomers = async (
  filters: ServiceIndividualFilterParams,
): Promise<ServiceValueCustomersResponse> => {
  const response = await cachedDashboardGet<
    ApiResponseEnvelope<ServiceValueCustomersResponse>
  >("/dashboard/service/individual/low-value-customers",
    buildServiceIndividualParams(filters),
  );

  return response.data;
};
export interface ServiceDashboardBootstrapResponse {
  view: string;
  kpis: ServiceIndividualKpiResponse;
  performance: PerformanceOverviewResponse;
  operationalInsights: OperationalInsightsResponse;
  inventory: InventorySnapshotResponse;
  customerDetails: ServiceCustomerDetailsResponse;
  valueCustomers: ServiceValueCustomersResponse;
  charts: unknown | null;
}

export const fetchServiceDashboardBootstrap = async (
  filters: ServiceIndividualFilterParams,
  view = "individual",
  comparisonPeriod?: string,
): Promise<ServiceDashboardBootstrapResponse> => {
  const params: Record<string, string> = {
    ...buildServiceIndividualParams(filters),
    view,
  };

  if (comparisonPeriod) params.comparison_period = comparisonPeriod;

  const response = await cachedDashboardGet<ApiResponseEnvelope<ServiceDashboardBootstrapResponse>>(
    "/dashboard/service/bootstrap",
    params,
  );

  return response.data;
};


